import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, shareReplay} from 'rxjs/operators';

import {HttpService} from 'src/app/services/http.service';
import {InvestorContactReqRes} from 'src/app/dashboard/contacts/models/investorContactReqRes.model';
import {HoldingSearchOptions} from 'src/app/dashboard/shared/holding/HoldingSearchOptions.model';
import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import {SearchOptionsResponse} from 'src/app/shared/models/SearchOptionsResponse.model';
import { InvestingEntityReqRes } from 'src/app/dashboard/models/InvestingEntityReqRes.model';

@Injectable()
export class GpHoldingDataService {
  public readonly HOLDING_BASE_ENDPOINT = 'gp/holdings';

  constructor(private http: HttpService) {
  }

  public getList(
    options: HoldingSearchOptions
  ): Observable<SearchOptionsResponse<GpHoldingListItemReqRes>> {
    const querystring = options
      ? this.getSearchOptionsToQuerystring(options)
      : '';
    return this.http
      .getTyped<SearchOptionsResponse<GpHoldingListItemReqRes>>(
        this.HOLDING_BASE_ENDPOINT + querystring
      )
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public getListAll(includeDrafts: boolean = false): Observable<SearchOptionsResponse<GpHoldingListItemReqRes>> {
    const searchOptions = new HoldingSearchOptions(10000);
    searchOptions.includeApplicationId = true;
    searchOptions.includeDrafts = includeDrafts;
    return this.getList(searchOptions);
  }

  isFileSupportedOfferingDeck(metaFileLinkId: number) {
    const url = `${this.HOLDING_BASE_ENDPOINT}/file-supported-offering-deck/${metaFileLinkId}`;
    return this.http.getTyped<boolean>(url).pipe(shareReplay());
  }

  isFileSupportedPhoto(metaFileLinkId: number) {
    const url = `${this.HOLDING_BASE_ENDPOINT}/file-supported-photo/${metaFileLinkId}`;
    return this.http.getTyped<boolean>(url).pipe(shareReplay());
  }

  isFileSupportedPaymentRequestDocument(metaFileLinkId: number) {
    const url = `${this.HOLDING_BASE_ENDPOINT}/file-supported-payment-request-document/${metaFileLinkId}`;
    return this.http.getTyped<boolean>(url).pipe(shareReplay());
  }

  getInvestedContacts(holdingId: number) {
    const url = `${this.HOLDING_BASE_ENDPOINT}/${holdingId}/invested`;
    return this.http.getTyped<InvestorContactReqRes[]>(url);
  }

  getContactsWhoCanSeeTheHoldingInInvestorPortal(holdingId: number) {
    const url = `${this.HOLDING_BASE_ENDPOINT}/${holdingId}/contacts-can-view-holding`;
    return this.http.getTyped<InvestorContactReqRes[]>(url).pipe(shareReplay());
  } // DEPRECATED


  getAccessibleByInvestingEntities(holdingId: number) {
    const url = `${this.HOLDING_BASE_ENDPOINT}/${holdingId}/accessible-by-investing-entities`;
    return this.http.getTyped<InvestingEntityReqRes[]>(url).pipe(shareReplay());
  }

  protected getSearchOptionsToQuerystring(
    options: HoldingSearchOptions
  ): string {
    return `?filter=${options.filter ? options.filter : ''}
    &sortOrder=${options.sortOrder ? options.sortOrder : ''}
    &pageNumber=${options.pageNumber ? options.pageNumber : 0}
    &pageSize=${options.pageSize || 1000}&orderBy=${options.orderBy}
    &includeDrafts=${options.includeDrafts}
    &includeApplicationId= ${options.includeApplicationId}`;
  }

  public moveToArchive(holdingId:number): Observable<void>{
    const url = `${this.HOLDING_BASE_ENDPOINT}/${holdingId}/move-to-archive`;
    return this.http.postTyped<void>(url, null).pipe(shareReplay());
  }
}
